import {
  AfterViewInit,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { Assistance } from '../../../../../models/assistance'
import { AssistantAvailability } from '../../../../../models/assistantAvailability'
import { AssistanceStatus } from '../../../../../models/assistanceStatus'
import {
  IonicSelectableCloseButtonTemplateDirective,
  IonicSelectableComponent,
  IonicSelectableFooterTemplateDirective,
  IonicSelectableHeaderTemplateDirective,
  IonicSelectableIconTemplateDirective,
  IonicSelectableItemTemplateDirective,
  IonicSelectableSearchFailTemplateDirective,
  IonicSelectableValueTemplateDirective,
} from 'ionic-selectable'
import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs'
import { AssistantAvailabilitySelectItem, AssistantAvailabilitySelectItemFlag } from './AssistantAvailabilitySelectItem'
import { filter, first, map, skipWhile, switchMap, takeUntil, tap } from 'rxjs/operators'
import { AssistanceService } from '../../../../../services/domain/assistance/assistance.service'
import { ClientService } from '../../../../../services/domain/client/client.service'
import { AssistantAvailabilitiesRecommendationService } from '../../../../../services/assistant-availabilities-recommendation.service'
import {
  IRecommendedAssistantsFilter,
  RecommendedAssistantsFilter,
} from '../../../../assistance/assistance-editor/recommended-assistants-filter'
import { SearchUtils } from '../../../../../shared/utils/searchUtils'
import { AssistantAvailabilitiesService } from '../../../../../services/domain/assistant-availabilities/assistant-availabilities.service'
import { AssistanceStash } from './AssistanceStash'
import { ListAssistanceLockService } from '../../../../assistance/assistance-list-new/list-assistance-lock.service'
import { DateRange, DateUtils } from '../../../../../services/date-utils.service'
import { FeatureFlags } from '../../../../../shared/featureFlags'
import { differenceInMinutes } from 'date-fns'
import { UsersService } from '../../../../../services/domain/users.service'
import { NgxFloatUiContentComponent } from 'ngx-float-ui'
import { AssistantService } from '../../../../../services/domain/assistant/assistant.service'
import { FloatoverService } from '../../../../floatover/floatover.service'
import { faFilter, faSlash } from '@fortawesome/free-solid-svg-icons'
import { AvailabilitySelectItemsStash } from './AvailabilitySelectItemsStash'
import { TouchscreenService } from '../../../../../services/touchscreen.service'
import { IonicModule } from '@ionic/angular'
import { AsyncPipe, DatePipe, NgClass, NgIf } from '@angular/common'
import { FormsModule } from '@angular/forms'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { GenderSelectComponent } from '../../../../assistance/assistance-editor/gender-select/gender-select.component'
import { log } from '../../../../../shared/utils/rxjsUtils'

type AssistantSelectMode = 'availabilities' | 'allAssistants'

@Component({
  selector: 'app-assistant-select',
  templateUrl: './assistant-select.component.html',
  styleUrls: ['./assistant-select.component.scss'],
  imports: [
    IonicModule,
    DatePipe,
    NgClass,
    FormsModule,
    NgIf,
    AsyncPipe,
    FontAwesomeModule,
    GenderSelectComponent,
    IonicSelectableComponent,
    IonicSelectableSearchFailTemplateDirective,
    IonicSelectableValueTemplateDirective,
    IonicSelectableIconTemplateDirective,
    IonicSelectableFooterTemplateDirective,
    IonicSelectableItemTemplateDirective,
    IonicSelectableCloseButtonTemplateDirective,
    IonicSelectableHeaderTemplateDirective,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AssistantSelectComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() assistanceURID: string
  @Input() assistanceRange: DateRange // Optional. Will fallback to assistance's planned range.
  @Input() isEnabled: boolean
  @Input() isModal = false
  @Input() hideStatusButtons: boolean = false
  @Input() filterOverrides: Partial<IRecommendedAssistantsFilter> = {}

  @Output() availabilitiesChanged = new EventEmitter<{
    assistance: Assistance
    availabilities: AssistantAvailability[]
  }>()
  @Output() availabilitiesRemoved = new EventEmitter<{ assistanceId: number }>()
  @Output() statusChanged = new EventEmitter<{
    availability: AssistantAvailability
    status: AssistanceStatus
  }>()
  @Output() statusClicked = new EventEmitter<AssistantAvailability>()
  @Output() closed = new EventEmitter()
  @Output() closedWithoutChanges = new EventEmitter()

  @ViewChild('assistantSelectComponent')
  assistantSelectComponent: IonicSelectableComponent
  @ViewChild('tooltip') tooltip: NgxFloatUiContentComponent

  private TAG: string = 'AssistantSelectInternalComponent'

  private unsubscribe: Subject<void> = new Subject<void>()

  private assistanceURID$: ReplaySubject<string> = new ReplaySubject<string>(1)
  private filterChanged$: ReplaySubject<RecommendedAssistantsFilter> = new ReplaySubject<RecommendedAssistantsFilter>(1)
  selectMode$: BehaviorSubject<AssistantSelectMode> = new BehaviorSubject<AssistantSelectMode>('availabilities')
  private onSearch$: BehaviorSubject<{
    component: IonicSelectableComponent
    text: string
  }> = new BehaviorSubject({ component: null, text: '' })

  assistance: Assistance
  assistanceStash = new AssistanceStash()
  selectItemsStash = new AvailabilitySelectItemsStash([])
  availabilityItemStash = new AvailabilitySelectItemsStash()

  itemsAllAssistants: AssistantAvailabilitySelectItem[]

  itemsCurrentlyRendered: AssistantAvailabilitySelectItem[]
  itemsCurrentlyRenderedDisabled: AssistantAvailabilitySelectItem[]

  itemsSelected: AssistantAvailabilitySelectItem[] = []

  recommendedAssistantsFilter: RecommendedAssistantsFilter

  confirming = false

  isTouchscreen = false

  data$: Observable<unknown>
  private open$ = new BehaviorSubject<any>('')

  assistanceAroundInfoLoading = false
  availabilitiesLoading = false

  faFilter = faFilter
  faSlash = faSlash

  protected readonly AssistantAvailabilitySelectItemFlag = AssistantAvailabilitySelectItemFlag
  private modalClosing = false

  constructor(
    private assistanceService: AssistanceService,
    private availabilitiesRecommender: AssistantAvailabilitiesRecommendationService,
    private clientsService: ClientService,
    private availabilitiesService: AssistantAvailabilitiesService,
    private assistanceLocker: ListAssistanceLockService,
    private userService: UsersService,
    private assistantService: AssistantService,
    private floatoverService: FloatoverService,
    private touchscreenService: TouchscreenService,
  ) {
    this.data$ = this.loadData()
    this.isTouchscreen = this.touchscreenService.isTouchscreen
  }

  ngOnInit(): void {}

  ngAfterViewInit() {
    this.data$.subscribe((e) => console.log('data$', e))
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      if (changes.hasOwnProperty(propName) && propName === 'assistanceURID' && !!changes.assistanceURID.currentValue) {
        this.assistanceURID$.next(changes.assistanceURID.currentValue)
      }
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete()
  }

  /**
   * Sets up the assistance for all further operations.
   * Handles nonrecurrent and recurrent assistances, using URID.
   * @private
   */
  private setupAssistanceFromURID(urid: string): Observable<Assistance> {
    return this.assistanceService.getByURID(urid).pipe(map((a) => (this.assistance = a)))
  }

  protected getAssistanceRange(): DateRange {
    if (this.assistanceRange) return this.assistanceRange
    else if (this.assistance)
      return {
        // Custom date range takes precendence
        from: this.assistance.getDateFrom(),
        to: this.assistance.getDateTo(),
      } // Fallback to assistance's planned range
    return null
  }

  switchSelectMode(mode: AssistantSelectMode) {
    this.selectMode$.next(mode)
  }

  private setItemsSelectedFromAssistance(assistance: Assistance) {
    if (!assistance.assistantAssignments) return

    this.itemsSelected = assistance.assistantAssignments.getAvailabilitiesWithStatus(true).map((assignment) => {
      return new AssistantAvailabilitySelectItem({
        availability: assignment.availability,
        status: assignment.status,
        enabled: true,
      })
    })

    if (this.assistantSelectComponent) this.assistantSelectComponent._selectedItems = this.itemsSelected
  }

  private addToSelectedItems(items: AssistantAvailabilitySelectItem[]) {
    this.itemsSelected.push(
      ...items.filter((itemToAdd) => !this.itemsSelected.some((i) => i.availability.id === itemToAdd.availability.id)),
    )

    if (this.assistantSelectComponent) this.assistantSelectComponent._selectedItems = this.itemsSelected
  }

  private setItemsRendered(items: AssistantAvailabilitySelectItem[]) {
    this.itemsCurrentlyRendered = items
    this.itemsCurrentlyRenderedDisabled = items?.filter((i) => !i.enabled)
  }

  onSearch(event: { component: IonicSelectableComponent; text: string }) {
    this.onSearch$.next(event)
  }

  private search(
    items: AssistantAvailabilitySelectItem[],
    event: { component: IonicSelectableComponent; text: string },
  ) {
    event.component.startSearch()
    const filtered = this.filterAvailabilitiesByAssistantName(items, event.text)
    event.component.endSearch()

    return filtered
  }

  private filterAvailabilitiesByAssistantName(allAvailabilityItems: AssistantAvailabilitySelectItem[], text: string) {
    if (!text.length) return allAvailabilityItems
    return allAvailabilityItems.filter((a) =>
      SearchUtils.includeFilter(a.availability.assistant.worker.user.fullName(), text),
    )
  }

  onIonicSelectableClose($event: { component: IonicSelectableComponent }) {
    this.close()
    this.confirming = false
  }

  private close(withChanges = true) {
    if (this.assistantSelectComponent.isOpened) {
      this.assistantSelectComponent.close()
      if (this.isModal) {
        this.modalClosing = true
        withChanges ? this.closed.emit() : this.closedWithoutChanges.emit()
      }
      this.confirming = false
    }
  }

  onAssistantModalCloseByCrossButton() {
    if (this.assistantSelectComponent.isOpened) this.close(false)
  }

  confirm() {
    if (!this.confirming) {
      this.confirming = true
      this.assistantSelectComponent.confirm()
      this.save()
    }
  }

  async save() {
    this.assistanceLocker.lock(this.assistance)

    const availabilities: AssistantAvailability[] = this.itemsSelected.map((i) => i.availability)

    const emitValue = { assistance: this.assistance, availabilities }

    this.availabilitiesChanged.emit(emitValue)

    this.close()
  }

  public clearAssistant() {
    this.availabilitiesRemoved.emit({ assistanceId: this.assistance.id })
    this.clear()
  }

  public clear() {
    this.assistantSelectComponent.clear()
    if (this.assistantSelectComponent.isOpened) this.close()
  }

  onRecommendedAssistantsFilterChange() {
    this.filterChanged$.next(this.recommendedAssistantsFilter)
  }

  onStatusClicked(availability: AssistantAvailability) {
    this.statusClicked.emit(availability)
  }

  private initFilter(assistance: Assistance) {
    this.recommendedAssistantsFilter = RecommendedAssistantsFilter.createFromAssistance(
      assistance,
      this.filterOverrides,
    )
    this.onRecommendedAssistantsFilterChange()
  }

  resetFilterToAssistanceDefaults() {
    this.recommendedAssistantsFilter.resetToDefaults()
    this.onRecommendedAssistantsFilterChange()
  }

  private mergeSelectedWithRecommended(
    mode: AssistantSelectMode,
    recommendedItems: AssistantAvailabilitySelectItem[],
    selectedItems: AssistantAvailabilitySelectItem[],
  ) {
    const toRender: AssistantAvailabilitySelectItem[] = []

    toRender.push(...selectedItems)

    toRender.push(
      ...recommendedItems.filter(
        (rec) =>
          !selectedItems.some((sel) => {
            return (
              sel.availability.assistant.id === rec.availability.assistant.id &&
              sel.availability.dt_start?.getTime() === rec.availability.dt_start?.getTime() && // null and undefined have to be the same
              sel.availability.dt_end?.getTime() === rec.availability.dt_end?.getTime()
            ) // null and undefined have to be the same
          }),
      ),
    )

    return toRender.sort((a, b) => AssistantAvailability.sortByAssistantName(a.availability, b.availability))
  }

  private filterOutFakeAvailabilitiesWhenThereIsARealOne(
    availabilities: AssistantAvailabilitySelectItem[],
    fakeAvails: AssistantAvailabilitySelectItem[],
  ) {
    return fakeAvails.filter((fa) => {
      return !availabilities.some((a) => fa.availability.assistantId === a.availability.assistantId)
    })
  }

  // Caution: Calling open() manually on ionicSelectable does not fire onOpen event.
  private open() {
    this.assistantSelectComponent.open()
  }

  onOpen() {
    this.open$.next('')
  }

  public isClosed() {
    return !this.assistantSelectComponent.isOpened
  }

  private async addAssistancesBeforeAndAfterToRenderedItems(
    assistance: Assistance,
    availabilityItems: AssistantAvailabilitySelectItem[],
  ) {
    if (!FeatureFlags.assistantSelect_showAssistancesAround) return availabilityItems

    const user = await this.userService.getCurrentUserPromise()
    if (!user.config_allow_assistant_select_before_after) return availabilityItems

    // Pro kazdeho asistenta v items je potreba stahnout assignmenty => assistance plannedFrom-To casy
    // 1. Takze potrebuju idcka asistentu
    const assistantIds: number[] = []

    for (const i of availabilityItems) {
      if (i.enabled) assistantIds.push(i.availability.assistantId)
    }

    // 2. Stahnout asistence v danem casovem rozmezi, ktere maji nektere z idcek asistentu
    this.assistance.getDateFrom()
    const range: DateRange = this.getAssistanceRange()

    ;(await this.assistanceService.getAssistancesAroundRangeForAssistants(range, 180, assistantIds))
      .pipe(first())
      .subscribe((assistances) => {
        // Pro kazdeho asistenta (item) najit odpovidajici asistence around a navesit je
        for (const i of availabilityItems) {
          const assistancesForAssistant = assistances.filter((a) =>
            a.assistantAssignments.hasAssistant(i.availability.assistantId),
          )

          for (const a of assistancesForAssistant) {
            if (a.getDateFrom() >= assistance.getDateTo()) {
              // Check jestli uz nahodou nemame nejakou blizsi assistanceAfter nez tato
              if (!i.assistanceAfter || i.assistanceAfter.getDateFrom() > a.getDateFrom()) {
                i.assistanceAfter = a
                i.assistanceAfterHHMM = differenceInMinutes(a.getDateFrom(), assistance.getDateTo()).toString(10)
              }
            }
            if (a.getDateTo() <= assistance.getDateFrom()) {
              // Check jestli uz nahodou nemame nejakou blizsi assistanceBefore nez tato
              if (!i.assistanceBefore || i.assistanceBefore.getDateTo() < a.getDateTo()) {
                i.assistanceBefore = a
                i.assistanceBeforeHHMM = differenceInMinutes(a.getDateTo(), assistance.getDateFrom()).toString(10)
              }
            }
          }
        }
      })
  }

  async showAssistanceBeforeInfo(availItem: AssistantAvailabilitySelectItem, event?: MouseEvent) {
    const currentMouseX = event?.pageX || 0
    const currentMouseY = event?.pageY || 0

    this.floatoverService.setLoading(true)

    this.floatoverService.setPosition(currentMouseX, currentMouseY)
    this.floatoverService.show()

    this.clientsService
      .getById(availItem.assistanceBefore.clientId)
      .pipe(first())
      .subscribe((client) => {
        const timeFrom = DateUtils.dateToHhColonMm(availItem.assistanceBefore.getDateFrom())
        const timeTo = DateUtils.dateToHhColonMm(availItem.assistanceBefore.getDateTo())

        this.floatoverService.setLoading(false)
        this.floatoverService.setContentHtml(
          `<div>${client.user.fullName()}</div><div><strong>${timeFrom}-${timeTo}</strong></div><div>${availItem.assistanceBefore.addressEnd}</div>`,
        )
      })
  }

  hideAssistanceAroundInfo() {
    this.floatoverService.hide()
  }

  showAssistanceAfterInfo(availItem: AssistantAvailabilitySelectItem, event?: MouseEvent) {
    const currentMouseX = event?.pageX || 0
    const currentMouseY = event?.pageY || 0

    this.floatoverService.setLoading(true)

    this.floatoverService.setPosition(currentMouseX, currentMouseY)
    this.floatoverService.show()

    this.clientsService
      .getById(availItem.assistanceAfter.clientId)
      .pipe(first())
      .subscribe((client) => {
        const timeFrom = DateUtils.dateToHhColonMm(availItem.assistanceAfter.getDateFrom())
        const timeTo = DateUtils.dateToHhColonMm(availItem.assistanceAfter.getDateTo())

        this.floatoverService.setLoading(false)
        this.floatoverService.setContentHtml(
          `<div>${client.user.fullName()}</div><div><strong>${timeFrom}-${timeTo}</strong></div><div>${availItem.assistanceAfter.addressStart}</div>`,
        )
      })
  }

  showAvailabilityNoteFloatover(availItem: AssistantAvailabilitySelectItem, event?: MouseEvent) {
    const currentMouseX = event?.pageX || 0
    const currentMouseY = event?.pageY || 0

    const noteContent = availItem?.availability?.note || 'Žádná poznámka'

    this.floatoverService.setContentHtml(
      `<div><strong>Poznámka asistenta:</strong></div><br/><div>${noteContent}</div>`,
    )

    // Store the position and show the floatover
    this.floatoverService.setPosition(currentMouseX, currentMouseY)
    this.floatoverService.show()
    this.floatoverService.setLoading(false)
    this.floatoverService.setContentHtml(
      `<div><strong>Poznámka asistenta:</strong></div><br/><div>${availItem.availability.note}</div>`,
    )
  }
  hideAvailabilityNoteFloatover() {
    this.floatoverService.hide()
  }

  private loadData(): Observable<unknown> {
    let assistanceRange: DateRange

    return combineLatest([this.assistanceURID$, this.open$]).pipe(
      // tap(([props, _]) => this.assistanceStash.stash(props as Assistance)),
      // skipWhile(_ => !this.assistanceStash.isChanged()),
      tap((_) => {
        if (this.isModal && !this.modalClosing) this.open()
      }),
      switchMap(([urid, _]) => this.setupAssistanceFromURID(urid)),

      tap((_) => {}),

      tap((assistance) => {
        this.setItemsSelectedFromAssistance(assistance)
        this.initFilter(assistance)
      }),

      // here we need to wait until the window is open and then continue the pipeline

      skipWhile((_) => !this.assistantSelectComponent.isOpened),

      tap((_) => {}),

      tap((_) => (this.availabilitiesLoading = true)),
      switchMap((assistance) => {
        assistanceRange = this.getAssistanceRange()
        return this.assistantService.getAllActiveAssistantsWithAssistanceInRange(assistanceRange)
      }),

      switchMap((assistantsToDisable) => {
        return combineLatest([
          this.availabilitiesRecommender.getAvailabilitiesInRange(assistanceRange, assistantsToDisable),
          this.availabilitiesRecommender.getAvailabilitiesForAllActiveAssistants(assistanceRange, assistantsToDisable),
        ])
      }),

      map(([availabilities, allAssistants]) => [
        availabilities,
        this.filterOutFakeAvailabilitiesWhenThereIsARealOne(availabilities, allAssistants),
      ]),
      switchMap(([availabilities, allAssistants]) =>
        this.filterChanged$.pipe(
          log(),
          map((filtr) => {
            if (!filtr) return [availabilities, allAssistants]
            return [
              this.availabilitiesRecommender.filterItems(filtr, availabilities),
              this.availabilitiesRecommender.filterItems(filtr, allAssistants),
            ]
          }),
        ),
      ),
      switchMap(([availabilities, allAssistants]) =>
        this.onSearch$.pipe(
          map((searchEvent) => {
            if (!searchEvent.text.length) return [availabilities, allAssistants]
            return [this.search(availabilities, searchEvent), this.search(allAssistants, searchEvent)]
          }),
        ),
      ),
      switchMap(([availabilities, allAssistants]) =>
        this.selectMode$.pipe(
          map((mode) => {
            if (mode === 'availabilities') return [mode, availabilities]
            if (mode === 'allAssistants') return [mode, allAssistants]
          }),
        ),
      ),
      map(([mode, items]) => {
        this.addToSelectedItems(this.selectItemsStash.current)
        return this.mergeSelectedWithRecommended(mode, items, this.itemsSelected)
      }),
      tap((items) => this.availabilityItemStash.stash(items)),
      filter((_) => this.availabilityItemStash.isChanged()),
      tap((items: AssistantAvailabilitySelectItem[]) => this.setItemsRendered(items)),

      tap((_) => (this.availabilitiesLoading = false)),
      tap((items: AssistantAvailabilitySelectItem[]) =>
        this.addAssistancesBeforeAndAfterToRenderedItems(this.assistance, items),
      ),

      takeUntil(this.unsubscribe),
    )
  }

  onClickSelect(event: { component: IonicSelectableComponent; item: any; isSelected: boolean }) {
    // this.stashSelection()

    this.selectItemsStash.stash(event.component._selectedItems)
  }
}
